import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cancelPlan, setPlanAutoRenew, upgradePlan } from "@/api/tier";
import type { UpgradePlanRequest } from "@/interfaces/tier";

/**
 * The three Plan-tab mutations (DIV-7761/DIV-7811): upgrade, cancel, setAutoRenew.
 *
 * Results are discriminated unions on `status` — a business failure (card
 * declined) is a RESOLVED mutation with { status: "error", message } (HTTP
 * 200), not a thrown error, so callers render it inline from the value:
 *
 *   const { upgrade } = usePlanActions();
 *   const result = await upgrade.mutateAsync({ planId: "pro", paymentMethodId });
 *   if (result.status === "error") showInlineError(result.message); // keep drawer open
 *
 * Confirmed successes invalidate ["tier"], so the badge, Plan tab, seat-limit
 * copy, and campaign-builder gates re-read GET /tier without a reload (PRD F8).
 */
export function usePlanActions() {
  const queryClient = useQueryClient();

  /**
   * Everything that names the organization's plan, refreshed together.
   *
   * `["tier"]` alone was not enough and had not been since the entitlement layer landed. The Plan
   * panel and every campaign-builder gate read `["entitlements", organizationId]`, which nothing in
   * the app invalidated — so an admin who upgraded stayed on "Your current plan: Starter" and
   * stayed locked out of the Pro features they had just paid for, until the two-minute stale window
   * or a window refocus healed it behind their back.
   *
   * Prefix keys, so `["entitlements", 4]` and `["tier", "payment-method"]` are both covered without
   * this needing to know the organization id or enumerate sub-keys.
   */
  function invalidateTierOnSuccess(result: { status: "success" | "error" }) {
    if (result.status !== "success") return;
    queryClient.invalidateQueries({ queryKey: ["tier"] });
    queryClient.invalidateQueries({ queryKey: ["entitlements"] });
    // The GFG-side Subscription tab, so a plan change shows there without a reload too.
    queryClient.invalidateQueries({ queryKey: ["orgSubscription"] });
  }

  /** POST /tier/upgrade — Starter → Pro (drawer PAY button). */
  const upgrade = useMutation({
    mutationFn: (body: UpgradePlanRequest) => upgradePlan(body),
    onSuccess: invalidateTierOnSuccess,
  });

  /** POST /tier/cancel — schedules the downgrade at period end (modal confirm). */
  const cancel = useMutation({
    mutationFn: () => cancelPlan(),
    onSuccess: invalidateTierOnSuccess,
  });

  /** PATCH /tier/auto-renew — toggle ON restores auto-renewal when scheduled. */
  const setAutoRenew = useMutation({
    mutationFn: (autoRenew: boolean) => setPlanAutoRenew(autoRenew),
    onSuccess: invalidateTierOnSuccess,
  });

  return { upgrade, cancel, setAutoRenew };
}
